import {SelectField, type SelectFieldOption} from '@wandb/components';
import React, {useCallback, useMemo} from 'react';
import {type Key} from 'react-aria-components';
import {Editor, type Element, type Node, Text, Transforms} from 'slate';
import {
  ReactEditor,
  type RenderElementProps,
  useReadOnly,
  useSelected,
  useSlateStatic,
} from 'slate-react';

import DOC_URLS from '../../../util/doc_urls';
import {getDeepLinkIdFromText} from '../util';
import {BlockWrapper} from './drag-drop';
import {type Heading, isHeading} from './headings';
import {
  getCollapsibleMarkdownHeading,
  isMarkdownBlock,
  markdownToTableOfContentsData,
} from './markdown-blocks-common';
import * as S from './table-of-contents.styles';

export interface TableOfContents extends Element {
  type: 'table-of-contents';
  displayLevel?: number;
}

export const isTableOfContents = (node: Node): node is TableOfContents =>
  node.type === 'table-of-contents';

export const TableOfContentsElement: React.FC<
  RenderElementProps & {
    element: TableOfContents;
  }
> = ({attributes, element, children}) => {
  const editor = useSlateStatic();
  const readOnly = useReadOnly();
  const selected = useSelected();

  const handleDisplayLevelChange = useCallback(
    (key: Key | null) => {
      if (key == null) {
        return;
      }
      const path = ReactEditor.findPath(editor, element);
      const range = Editor.range(editor, path);

      Editor.withoutNormalizing(editor, () => {
        Transforms.setNodes(
          editor,
          {displayLevel: Number(key)},
          {
            at: range,
          }
        );
      });
    },
    [editor, element]
  );

  const content = useMemo(() => {
    if (
      editor.children.length === 0 ||
      !editor.children.some((node: Node) => isHeading(node))
    ) {
      // Show message when there is no heading in reports for table of contents
      return (
        <S.NoHeadingMessage>
          Add a heading to create links in this table of contents.{' '}
          <S.LearnMoreLink href={DOC_URLS.reports + '#how-to-edit-a-report'}>
            Learn more.
          </S.LearnMoreLink>
        </S.NoHeadingMessage>
      );
    }

    const maxLevel = Math.max(
      ...editor.children.map(node => (isHeading(node) ? node.level : 0))
    );

    const options: SelectFieldOption[] = [];

    if (maxLevel === 0) {
      return null;
    }
    if (maxLevel >= 1) {
      options.unshift({id: 1, label: 'Header 1 Only'});
    }
    if (maxLevel >= 2) {
      options.unshift({id: 2, label: 'Header 1 & 2'});
    }
    if (maxLevel >= 3) {
      options.unshift({id: 3, label: 'All Headers'});
    }

    // By default, it tries to show up to the highest header level in the reports
    const displayLevel = element.displayLevel ?? maxLevel;

    return (
      <>
        {editor.children
          .filter(
            (node: Node) =>
              ((isHeading(node) && node.level <= displayLevel) ||
                (isMarkdownBlock(node) &&
                  getCollapsibleMarkdownHeading(node.content) !== null)) &&
              node.children.length > 0
          )
          .map((node: Node, i: number) => {
            if (isMarkdownBlock(node)) {
              return markdownToTableOfContentsData(node)
                .filter(data => data.level <= displayLevel)
                .map(({id, level, heading}) => (
                  <S.Anchor key={id} href={'#' + id} level={level}>
                    {heading}
                  </S.Anchor>
                ));
            }
            const titleTexts = ((node as Heading).children as Node[])
              .filter(n => Text.isText(n))
              .map(n => n.text as string);

            const title = titleTexts.join('');
            const href = '#' + getDeepLinkIdFromText(title);

            return (
              <S.Anchor
                // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                key={i}
                href={href}
                level={(node as Heading).level as 1 | 2 | 3}>
                {title}
              </S.Anchor>
            );
          })}
        {!readOnly && displayLevel > 0 && (
          <S.HeaderSelect>
            <SelectField
              aria-label="Headers to include"
              items={options}
              value={displayLevel}
              onChange={handleDisplayLevelChange}
              fullWidth
            />
          </S.HeaderSelect>
        )}
      </>
    );
  }, [editor, element, handleDisplayLevelChange, readOnly]);

  return (
    <BlockWrapper attributes={attributes} element={element}>
      <S.TableOfContents contentEditable={false} selected={selected}>
        {content}
      </S.TableOfContents>
      {children}
    </BlockWrapper>
  );
};

export const withTableOfContents = <T extends Editor>(editor: T) => {
  const {isVoid} = editor;

  editor.isVoid = element => {
    return isTableOfContents(element) ? true : isVoid(element);
  };

  return editor;
};
